"use strict";
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    }
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
var WrappedResultError = /** @class */ (function (_super) {
    __extends(WrappedResultError, _super);
    function WrappedResultError(message, originalError) {
        var _this = _super.call(this, message) || this;
        _this.originalError = originalError;
        return _this;
    }
    return WrappedResultError;
}(Error));
exports.WrappedResultError = WrappedResultError;
function valueIsError(value) {
    return value.type === "error";
}
function valueIsOk(value) {
    return value.type === "value";
}
var Result = /** @class */ (function () {
    function Result(value) {
        this.value = value;
    }
    /**
     * Gets the underlying value. Note that you should check whether the value is Ok before doing this, or else a @see WrappedResultError may be thrown.
     */
    Result.prototype.getValue = function () {
        if (valueIsError(this.value)) {
            throw new WrappedResultError("Attempted to get the raw value of a Result, but the value was an Error.", this.value.error);
        }
        return this.value.value;
    };
    /**
     * Gets the underlying error. Note that you should check whether the value is Error before doing this, or else a @see WrappedResultError may be thrown.
     */
    Result.prototype.getError = function () {
        if (!valueIsError(this.value)) {
            var type = typeof this.value.value;
            throw new Error("Attempted to get the Error of a Result, but the value was not an Error. Value type was " + type + ".");
        }
        return this.value.error;
    };
    /**
     * Checks whether the value is Error.
     */
    Result.prototype.isError = function () {
        return valueIsError(this.value);
    };
    /**
     * Checks whether the value is Ok.
     */
    Result.prototype.isOk = function () {
        return !this.isError();
    };
    /**
     * Maps the Ok value to another value. Will only run if the value is Ok.
     */
    Result.prototype.map = function (mapper) {
        if (valueIsOk(this.value)) {
            return Result.ofValue(mapper(this.value.value));
        }
        return Result.ofError(this.value.error);
    };
    /**
     * Maps the Error value back to an OK value. Will only run if the value is Error.
     */
    Result.prototype.mapError = function (mapper) {
        if (valueIsError(this.value)) {
            return Result.ofValue(mapper(this.value.error));
        }
        return this;
    };
    /**
     * Binds the value to the result returned by the @param mapper function. Will only run if the value is Ok.
     */
    Result.prototype.bind = function (mapper) {
        if (valueIsOk(this.value)) {
            return mapper(this.value.value);
        }
        return Result.ofError(this.value.error);
    };
    /**
     * Binds the result to the one returned by the @param mapper function. Will only run if the value is Error.
     */
    Result.prototype.bindError = function (mapper) {
        if (valueIsError(this.value)) {
            return mapper(this.value.error);
        }
        return Result.ofValue(this.value.value);
    };
    /**
     * Passes the value to the given @param fn function where it can then be used for work that doesn't return a value. Will only run if the value is Ok.
     */
    Result.prototype.iter = function (fn) {
        if (valueIsOk(this.value)) {
            fn(this.value.value);
        }
        return this;
    };
    /**
     * Executes the given @param fn function when the result is Error.
     */
    Result.prototype.iterError = function (fn) {
        if (valueIsError(this.value)) {
            fn(this.value.error);
        }
        return this;
    };
    /**
     * Returns the @param defaultValue if the Result is Error, else returns the Ok value.
     * @example
     * console.log(Result.ofValue(5).defaultValue(10)) // 5
     * console.log(Result.ofError(new Error("Test error")).defaultValue(10)) // 10
     */
    Result.prototype.defaultValue = function (defaultValue) {
        if (valueIsOk(this.value)) {
            return this.value.value;
        }
        return defaultValue;
    };
    /**
     * Wraps the @param value in an Ok Result.
     */
    Result.ofValue = function (value) {
        return new Result({
            type: "value",
            value: value
        });
    };
    /**
     * Wraps the @param error in an Error Result.
     */
    Result.ofError = function (error) {
        return new Result({
            type: "error",
            error: error
        });
    };
    /**
     * Takes the given promise or function that returns a promise and wraps it in a try/catch. Returns Result.ofError if it throws an error.
     */
    Result.ofPromise = function (promise) {
        var computation = typeof promise === "function" ? promise() : promise;
        return computation
            .then(function (x) {
            var value = {
                type: "value",
                value: x
            };
            return value;
        })
            .catch(function (e) {
            var error = {
                type: "error",
                error: e
            };
            return error;
        })
            .then(function (r) { return (valueIsError(r) ? Result.ofError(r.error) : Result.ofValue(r.value)); });
    };
    /**
     * Takes the given function and executes it, returning Result.ofError if it throws an error.
     */
    Result.ofFunction = function (computation) {
        try {
            return Result.ofValue(computation());
        }
        catch (e) {
            return Result.ofError(e);
        }
    };
    /**
     * Gets the underlying value of the Result. Note that you should check whether the value is Ok before doing this, or else a @see WrappedResultError may be thrown.
     */
    Result.getValue = function (result) {
        return result.getValue();
    };
    /**
     * Gets the underlying error of the Result. Note that you should check whether the value is Error before doing this, or else a @see WrappedResultError may be thrown.
     */
    Result.getError = function (result) {
        return result.getError();
    };
    /**
     * Checks whether the Result is Error.
     */
    Result.isError = function (result) {
        return result.isError();
    };
    /**
     * Checks whether the Result is Ok.
     */
    Result.isOk = function (result) {
        return result.isOk();
    };
    /**
     * Returns a curried function that will map the Ok value to another value. Will only run if the value is Ok.
     */
    Result.map = function (mapper) {
        return function (r) { return r.map(mapper); };
    };
    /**
     * Returns a curried function that will map the Error value back to an OK value. Will only run if the value is Error.
     */
    Result.mapError = function (mapper) {
        return function (r) { return r.mapError(mapper); };
    };
    /**
     * Returns a curried function that will bind the value to the result returned by the @param mapper function. Will only run if the value is Ok.
     */
    Result.bind = function (mapper) {
        return function (r) { return r.bind(mapper); };
    };
    /**
     * Returns a curried function that will bind the result to the one returned by the @param mapper function. Will only run if the value is Error.
     */
    Result.bindError = function (mapper) {
        return function (r) { return r.bindError(mapper); };
    };
    /**
     * Returns a curried function that will pass the value to the given @param fn function where it can then be used for work that doesn't return a value. Will only run if the value is Ok.
     */
    Result.iter = function (fn) {
        return function (r) { return r.iter(fn); };
    };
    /**
     * Returns a curried function that will execute the given @param fn function when the result is Error.
     */
    Result.iterError = function (fn) {
        return function (r) { return r.iterError(fn); };
    };
    return Result;
}());
exports.Result = Result;
//# sourceMappingURL=result.js.map